import { Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useDepartment } from './context/DepartmentContext'
import { useAuth } from './context/AuthContext'
import Layout from './components/layout/Layout'
import PublicLayout from './components/public/PublicLayout'
import DepartmentSelector from './pages/DepartmentSelector'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Ingestion from './pages/Ingestion'
import DailyOps from './pages/DailyOps'
import Analysis from './pages/Analysis'
import Flags from './pages/Flags'
import Reports from './pages/Reports'
import Admin from './pages/Admin'
import PosTerminal from './pages/pos/PosTerminal'
import ProductManagement from './pages/pos/ProductManagement'
import Inventory from './pages/pos/Inventory'
import Suppliers from './pages/pos/Suppliers'
import Purchases from './pages/pos/Purchases'
import CostingEngine from './pages/pos/CostingEngine'
import CostingPage from './pages/CostingPage'
import BusinessAnalytics from './pages/BusinessAnalytics'
import Overhead from './pages/pos/Overhead'
import FinancialReports from './pages/pos/FinancialReports'
import FlagsConsole from './pages/pos/FlagsConsole'
import Accounting from './pages/pos/Accounting'
import Intelligence from './pages/pos/Intelligence'
import StaffOps from './pages/pos/StaffOps'
import TillManagement from './pages/TillManagement'
import Recruitment from './pages/Recruitment'
import Home from './pages/public/Home'
import MenuPage from './pages/public/Menu'
import Gallery from './pages/public/Gallery'
import Contact from './pages/public/Contact'
import Careers from './pages/public/Careers'
import OurStory from './pages/public/OurStory'
import OrderPage from './pages/public/Order'
import OrderPageFull from './pages/public/Order.full'
import OrderSuccess from './pages/public/OrderSuccess'
import OrderCheckout from './pages/public/OrderCheckout'
import OrderCart from './pages/public/OrderCart'
import ReviewPage from './pages/public/Review'
import { ChefHat, Loader, LogOut, Clock, WifiOff, RefreshCw } from 'lucide-react'
import { ADMIN_EMAILS } from './config/adminConfig'

function StaffApp() {
  const { isDepartmentSelected } = useDepartment()
  const { user, userProfile, loading, logout, connectionError, refreshProfile } = useAuth()
  const isHardcodedAdmin = ADMIN_EMAILS.includes(user?.email)

  // Display-only timeout: shows a Retry button if loading is still true
  // after 20 seconds. This does NOT touch the auth flow at all — auth
  // continues running in AuthContext exactly as it always has. We're
  // only changing what's rendered while waiting, so the user has a
  // recovery path if Firebase Auth never resolves on a broken mobile
  // network. If `loading` flips to false at any point (auth resolves),
  // the cleanup runs and the spinner is replaced by the normal app.
  const [showRetry, setShowRetry] = useState(false)
  useEffect(() => {
    if (!loading) {
      setShowRetry(false)
      return
    }
    const timer = setTimeout(() => setShowRetry(true), 20000)
    return () => clearTimeout(timer)
  }, [loading])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        {showRetry ? (
          <div className="text-center p-8 max-w-md">
            <p className="text-white text-lg mb-2">Taking longer than expected</p>
            <p className="text-gray-400 text-sm mb-6">
              Check your internet connection and try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-lg text-sm font-medium"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="text-center">
            <Loader size={32} className="animate-spin text-brand-400 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Loading...</p>
          </div>
        )}
      </div>
    )
  }

  if (!user) {
    return <Login />
  }

  // Connection error — Firestore is unreachable (admin bypasses this)
  if (connectionError && !userProfile && !isHardcodedAdmin) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <WifiOff size={48} className="text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Connection Error</h1>
          <p className="text-gray-400 text-sm mb-6">
            Unable to reach the database. Please check your internet connection and try again.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => refreshProfile()}
              className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-5 py-2.5 rounded-lg text-sm font-medium"
            >
              <RefreshCw size={16} /> Retry
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-300 px-5 py-2.5 rounded-lg text-sm font-medium"
            >
              <LogOut size={16} /> Log Out
            </button>
          </div>
        </div>
      </div>
    )
  }

  const hasAccess = isHardcodedAdmin || (userProfile && (
    userProfile.role === 'admin' ||
    (userProfile.allowedDepartments && userProfile.allowedDepartments.length > 0) ||
    userProfile.isApproved
  ))

  if (!isHardcodedAdmin && userProfile && !hasAccess) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <ChefHat className="inline-block text-brand-400 mb-2" size={48} />
          <h1 className="text-2xl font-bold text-white mb-1">
            DESRES KITCHEN AND CONFECTIONERY
          </h1>
          <div className="w-48 h-px bg-gray-700 mx-auto mb-6" />
          <div className="bg-gray-900 border border-amber-800 rounded-xl p-6">
            <Clock size={32} className="text-amber-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-white mb-2">Account Pending Approval</h2>
            <p className="text-gray-400 text-sm mb-6">
              Your account is pending approval. Contact an admin to get access.
            </p>
            <button
              onClick={logout}
              className="flex items-center justify-center gap-2 mx-auto bg-gray-800 hover:bg-gray-700 text-gray-300 px-4 py-2 rounded-lg text-sm transition-colors"
            >
              <LogOut size={14} />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!isDepartmentSelected) {
    return <DepartmentSelector />
  }

  return (
    <Layout>
      <Routes>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="ingestion" element={<Ingestion />} />
        <Route path="daily" element={<Navigate to="/app/ingestion" replace />} />
        <Route path="analysis" element={<Navigate to="/app/analytics" replace />} />
        <Route path="flags" element={<Flags />} />
        <Route path="reports" element={<Reports />} />
        <Route path="admin" element={
          (isHardcodedAdmin || userProfile?.role === 'admin')
            ? <Admin />
            : <Navigate to="/app/dashboard" replace />
        } />
        <Route path="pos" element={<PosTerminal />} />
        <Route path="products" element={<ProductManagement />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="purchases" element={<Purchases />} />
        <Route path="costing" element={<CostingEngine />} />
        <Route path="dept-costing" element={<CostingPage />} />
        <Route path="analytics" element={<BusinessAnalytics />} />
        <Route path="overhead" element={<Overhead />} />
        <Route path="pos-reports" element={<FinancialReports />} />
        <Route path="pos-flags" element={<FlagsConsole />} />
        <Route path="accounting" element={<Accounting />} />
        <Route path="intelligence" element={<Intelligence />} />
        <Route path="staff-ops" element={<StaffOps />} />
        <Route path="till" element={<TillManagement />} />
        <Route path="recruitment" element={
          (isHardcodedAdmin || userProfile?.role === 'admin')
            ? <Recruitment />
            : <Navigate to="/app/dashboard" replace />
        } />
        <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
      </Routes>
    </Layout>
  )
}

function AdminPreviewRoute() {
  const { user, loading } = useAuth() || {}
  if (loading) {
    return (
      <div className="min-h-screen bg-umber flex items-center justify-center">
        <Loader size={24} className="animate-spin text-terracotta" />
      </div>
    )
  }
  if (!user || !ADMIN_EMAILS.includes(user.email)) {
    return <Navigate to="/" replace />
  }
  return <PublicLayout><OrderPageFull /></PublicLayout>
}

export default function App() {
  return (
    <Routes>
      {/* Public website */}
      <Route path="/" element={<PublicLayout><OrderPage /></PublicLayout>} />
      <Route path="/our-story" element={<PublicLayout><OurStory /></PublicLayout>} />
      <Route path="/menu" element={<PublicLayout><MenuPage /></PublicLayout>} />
      <Route path="/gallery" element={<PublicLayout><Gallery /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/home" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/order" element={<Navigate to="/" replace />} />
      <Route path="/order-preview" element={<AdminPreviewRoute />} />
      <Route path="/order/success" element={<PublicLayout><OrderSuccess /></PublicLayout>} />
      <Route path="/order/checkout" element={<PublicLayout><OrderCheckout /></PublicLayout>} />
      <Route path="/order/cart" element={<PublicLayout><OrderCart /></PublicLayout>} />
      <Route path="/careers" element={<PublicLayout><Careers /></PublicLayout>} />
      <Route path="/reviews" element={<PublicLayout><ReviewPage /></PublicLayout>} />

      {/* Staff dashboard */}
      <Route path="/app/*" element={<StaffApp />} />

      {/* Legacy routes — redirect to /app */}
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/ingestion" element={<Navigate to="/app/ingestion" replace />} />
      <Route path="/daily" element={<Navigate to="/app/ingestion" replace />} />
      <Route path="/analysis" element={<Navigate to="/app/analytics" replace />} />
      <Route path="/flags" element={<Navigate to="/app/flags" replace />} />
      <Route path="/reports" element={<Navigate to="/app/reports" replace />} />
      <Route path="/admin" element={<Navigate to="/app/admin" replace />} />
    </Routes>
  )
}
